import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  limit as qLimit,
  startAfter,
  type Query,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "../firebase/firestore";
import type { Question } from "../models/question";

export type Page<T> = {
  items: T[];
  next: QueryDocumentSnapshot<DocumentData, DocumentData> | null;
};

function mapDoc(
  d: QueryDocumentSnapshot<DocumentData, DocumentData>
): Question {
  const v = d.data();

  const base: Question = {
    id: d.id,
    text: String(v.text ?? ""),
    dimensionKey: String(v.dimensionKey ?? ""),
    dimensionName: String(v.dimensionName ?? ""),
    variableIndex: Number(v.variableIndex ?? 0),
    variableName: String(v.variableName ?? ""),
    questionIndex: Number(v.questionIndex ?? 0),
    questionId: String(v.questionId ?? ""),
    evidenceRequired: Boolean(v.evidenceRequired ?? false),
    tags: Array.isArray(v.tags) ? v.tags.map(String) : [],
  };

  return {
    ...base,
    ...(typeof v.helpText === "string" && v.helpText.length > 0
      ? { helpText: v.helpText }
      : {}),
    ...(typeof v.weight === "number" ? { weight: v.weight } : {}),
    ...(typeof v.versionId === "string" ? { versionId: v.versionId } : {}),
    ...(v.createdAt !== undefined ? { createdAt: v.createdAt } : {}),
    ...(v.updatedAt !== undefined ? { updatedAt: v.updatedAt } : {}),
  };
}

/** Lista 30 preguntas por (dimensionKey, variableIndex). */
export async function listQuestionsPage(
  args: {
    dimensionKey?: string;
    variableIndex?: number;
    cursor?: QueryDocumentSnapshot<DocumentData, DocumentData> | null;
    limit?: number;
  } = {}
): Promise<Page<Question>> {
  const { dimensionKey, variableIndex, cursor, limit = 30 } = args;

  let q: Query<DocumentData, DocumentData> = collection(db, "questions");
  const wheres: Array<ReturnType<typeof where>> = [];
  if (dimensionKey) wheres.push(where("dimensionKey", "==", dimensionKey));
  if (typeof variableIndex === "number")
    wheres.push(where("variableIndex", "==", variableIndex));

  q = query(q, ...wheres, orderBy("questionIndex", "asc"), qLimit(limit));

  const q2 = cursor ? query(q, startAfter(cursor)) : q;
  const snap = await getDocs(q2);

  const items = snap.docs.map(mapDoc);

  let next: QueryDocumentSnapshot<DocumentData, DocumentData> | null = null;
  if (snap.docs.length === limit) {
    const last = snap.docs[snap.docs.length - 1]!;
    next = last;
  }

  return { items, next };
}
